import api from "@/api/api";
import {processObjectNullValues} from "@/common/processObjectNullValues";
import {formatIsoToCustom} from "@/common/formatToIso";
import {
    ADD_TICKET_ACTION, DELETE_TICKET_ACTION, SET_GROUP_OPTIONS,
    SET_LOADING,
    SET_PARENT_ACTION_OPTIONS,
    SET_STATUS_OPTIONS,
    SET_TICKET_ACTIONS, UPDATE_TICKET_ACTION,
    SET_CATEGORY_OPTIONS,
    SET_TYPE_OPTIONS
} from "@/store/mutation.types";

export default {
    namespaced: true,
    state: {
        ticketActions: [],
        groupOptions: [],
        statusOptions: [],
        parentActionOptions: [],
        categoryOptions: [],
        typeOptions: [],
        isLoading: false,
        meta: {
            current_page: 1,
            from: 0,
            last_page: 1,
            per_page: 15,
            to: 0,
            total: 0
        },
        links: {
            first: null,
            last: null,
            prev: null,
            next: null
        }
    },
    mutations: {
        SET_TICKET_ACTIONS(state, actions) {
            state.ticketActions = actions;
        },
        SET_GROUP_OPTIONS(state, options) {
            state.groupOptions = options;
        },
        SET_STATUS_OPTIONS(state, options) {
            state.statusOptions = options;
        },
        SET_PARENT_ACTION_OPTIONS(state, options) {
            state.parentActionOptions = options;
        },
        SET_CATEGORY_OPTIONS(state, options) {
            state.categoryOptions = options;
        },
        SET_TYPE_OPTIONS(state, options) {
            state.typeOptions = options;
        },
        SET_LOADING(state, loading) {
            state.isLoading = loading;
        },
        SET_META(state, meta) {
            state.meta = meta;
        },
        SET_LINKS(state, links) {
            state.links = links;
        },
        ADD_TICKET_ACTION(state, action) {
            state.ticketActions.push(action);
        },
        UPDATE_TICKET_ACTION(state, {index, action}) {
            state.ticketActions.splice(index, 1, action);
        },
        DELETE_TICKET_ACTION(state, actionId) {
            state.ticketActions = state.ticketActions.filter(item => item.id !== actionId);
        }
    },
    actions: {
        async getTicketGroups({commit}) {
            commit(SET_LOADING, true);
            try {
                const res = await api.tickets.getListUserGroups();
                const options = res.data?.data.map((group) => ({
                    value: group.id,
                    text: group.group_name
                }));
                commit(SET_GROUP_OPTIONS, options);
            } catch (e) {
                console.log(e);
                throw e;
            } finally {
                commit(SET_LOADING, false);
            }
        },
        async getTicketStatuses({commit}) {
            commit(SET_LOADING, true);
            try {
                const res = await api.tickets.getListActionStatuses();
                const options = res.data?.data.map((status) => ({
                    value: status.id,
                    text: status.name
                }));
                commit(SET_STATUS_OPTIONS, options);
            } catch (e) {
                console.log(e);
                throw e;
            } finally {
                commit(SET_LOADING, false);
            }
        },
        /**
         * Полный граф действий, без пагинации.
         *
         * Доске он нужен целиком: по нему считается, какими действиями тикет
         * можно перевести и в какой статус он после этого попадёт. С дефолтной
         * страницей в 15 записей часть переходов доске просто неизвестна —
         * карточка молча переставала перетаскиваться.
         */
        async getAllTicketActions({commit}) {
            commit(SET_LOADING, true);
            try {
                const collected = [];
                let page = 1;
                let lastPage = 1;

                do {
                    const res = await api.tickets.getListTicketActions({page, per_page: 100});
                    collected.push(...(res.data?.data || []));
                    lastPage = res.data?.meta?.last_page || 1;
                    page += 1;
                } while (page <= lastPage);

                commit(SET_TICKET_ACTIONS, collected.map((action) => processObjectNullValues({
                    id: action.id,
                    date: formatIsoToCustom(action.created_at),
                    name: action.name,
                    enabled: action.enabled ? "Да" : "Нет",
                    username: action.created_by?.username || action.created_by?.email,
                    title: action.title,
                    parent_action: collected.find(item => item.id === action.parent_action_id)?.name || "-",
                    parent_action_id: action.parent_action_id,
                    back_action: collected.find(item => item.id === action.back_action_id)?.name || "-",
                    back_action_id: action.back_action_id,
                    status: action.status?.name,
                    status_id: action.status_id ?? action.status?.id ?? null,
                    group_id: action.group_id
                })));
            } catch (e) {
                console.log(e);
                throw e;
            } finally {
                commit(SET_LOADING, false);
            }
        },
        async getTicketActions({commit, state}, params = {page: 1, per_page: 15}) {
            commit(SET_LOADING, true);
            try {
                const res = await api.tickets.getListTicketActions(params);
                const actions = res.data?.data?.map((action) => processObjectNullValues({
                    id: action.id,
                    date: formatIsoToCustom(action.created_at),
                    name: action.name,
                    enabled: action.enabled ? "Да" : "Нет",
                    username: action.created_by?.username || action.created_by?.email,
                    title: action.title,
                    parent_action: res.data?.data?.find(item => item.id === action.parent_action_id)?.name || "-",
                    parent_action_id: action.parent_action_id,
                    back_action: res.data?.data?.find(item => item.id === action.back_action_id)?.name || "-",
                    back_action_id: action.back_action_id,
                    status: action.status?.name,
                    status_id: action.status_id,
                    group_id: action.group_id
                }));
                commit(SET_TICKET_ACTIONS, actions);

                const parentOptions = res.data?.data?.map(action => ({
                    text: action.name,
                    value: action.id,
                }));
                commit(SET_PARENT_ACTION_OPTIONS, parentOptions);

                // Set pagination meta and links
                if (res.data?.meta) {
                    commit('SET_META', res.data.meta);
                }
                if (res.data?.links) {
                    commit('SET_LINKS', res.data.links);
                }
            } catch (e) {
                console.log(e);
                throw e;
            } finally {
                commit(SET_LOADING, false);
            }
        },
        async createAction({commit, state}, actionParams) {
            const errors = {
                name: 'Введите название',
                title: 'Введите заголовок',
                parent_action_id: 'Выберите родительское действие',
                group_id: 'Выберите группу',
                status_id: 'Выберите статус'
            };

            for (const [field, message] of Object.entries(errors)) {
                if (!actionParams[field]) {
                    throw new Error(message);
                }
            }

            try {
                const res = await api.tickets.createTicketAction(actionParams);
                const newAction = res.data?.data;
                const formattedAction = processObjectNullValues({
                    id: newAction.id,
                    date: formatIsoToCustom(newAction.created_at),
                    name: newAction.name,
                    enabled: newAction.enabled ? "Да" : "Нет",
                    username: newAction.created_by?.username || newAction.created_by?.email,
                    title: newAction.title,
                    parent_action: state.ticketActions.find(item => item.id === newAction.parent_action_id)?.name || "-",
                    parent_action_id: newAction.parent_action_id,
                    back_action: state.ticketActions.find(item => item.id === newAction.back_action_id)?.name || "-",
                    back_action_id: newAction.back_action_id,
                    status: newAction.status?.name,
                    status_id: newAction.status_id,
                    group_id: newAction.group_id,
                    _original: newAction
                });
                commit(ADD_TICKET_ACTION, formattedAction);
                this
            } catch (e) {
                console.log(e);
                throw e;
            }
        },
        async updateAction({commit, state}, {id, formData}) {
            try {
                const res = await api.tickets.updateTicketAction(id, {
                    name: formData.name,
                    title: formData.title,
                    parent_action_id: formData.parent_action_id,
                    status_id: formData.status_id,
                    group_id: formData.group_id,
                    enabled: formData.enabled,
                    back_action_id: formData.back_action_id === '-' ? null : formData.back_action_id,
                });

                const updatedAction = res.data?.data || formData;
                const index = state.ticketActions.findIndex(action => action.id === id);

                if (index !== -1) {
                    const parentActionName = state.ticketActions.find(
                        item => item.id === updatedAction.parent_action_id
                    )?.name || "-";

                    const backActionName = state.ticketActions.find(
                        item => item.id === updatedAction.back_action_id
                    )?.name || "-";

                    const oldItem = state.ticketActions[index];
                    const updated = processObjectNullValues({
                        id: updatedAction.id,
                        date: oldItem.date,
                        name: updatedAction.name,
                        title: updatedAction.title,
                        group_id: updatedAction.group_id,
                        status_id: updatedAction.status_id,
                        status: updatedAction.status?.name,
                        parent_action: parentActionName,
                        parent_action_id: updatedAction.parent_action_id,
                        back_action: backActionName,
                        back_action_id: updatedAction.back_action_id,
                        enabled: updatedAction.enabled ? "Да" : "Нет",
                        username: oldItem.username,
                        _original: updatedAction,
                    });
                    commit(UPDATE_TICKET_ACTION, {index, action: updated});
                }
            } catch (e) {
                console.log(`action update error ${e}`);
                throw e;
            }
        },
        async deleteAction({commit}, actionId) {
            try {
                await api.tickets.deleteTicketAction(actionId);
                commit(DELETE_TICKET_ACTION, actionId);
                await this.getTicketActions();
            } catch (e) {
                console.log(e);
                throw e;
            }
        },
        async getTicketCategories({commit}, params = {}) {
            commit(SET_LOADING, true);
            try {
                const res = await api.tickets.getListTicketCategories(params);
                const options = res.data?.data.map((category) => ({
                    value: category.id,
                    text: category.name
                }));
                commit(SET_CATEGORY_OPTIONS, options);
            } catch (e) {
                console.log(e);
                throw e;
            } finally {
                commit(SET_LOADING, false);
            }
        },
        async getTicketTypes({commit}, params = {}) {
            commit(SET_LOADING, true);
            try {
                const res = await api.tickets.getListTicketTypes(params);
                const options = res.data?.data.map((type) => ({
                    value: type.id,
                    text: type.name
                }));
                commit(SET_TYPE_OPTIONS, options);
            } catch (e) {
                console.log(e);
                throw e;
            } finally {
                commit(SET_LOADING, false);
            }
        }
    },
    getters: {
        ticketActions: state => state.ticketActions,
        groupOptions: state => state.groupOptions,
        statusOptions: state => state.statusOptions,
        parentActionOptions: state => state.parentActionOptions,
        categoryOptions: state => state.categoryOptions,
        typeOptions: state => state.typeOptions,
        isLoading: state => state.isLoading,
        meta: state => state.meta,
        links: state => state.links
    }
}